import { buildResponsiveVariantClassNames, resolveResponsiveValue, withResponsiveBaseFallback, } from '@smoothie-framework/ui-styles/styling';
import { styleValueToString } from '../internal/style';
import { computed } from '../state';
import { isReactiveAccessor } from '../state/accessor';
import { getTheme } from '../theme/theme_context';
export function splitVariantAndOtherProps(props, variantKeys) {
    const variants = {};
    const otherProps = {};
    for (const [key, value] of Object.entries(props)) {
        if (variantKeys.has(key)) {
            variants[key] = value;
            continue;
        }
        if (key !== 'children' && key !== 'className' && key !== 'style') {
            otherProps[key] = value;
        }
    }
    return { variants, otherProps };
}
export function buildComponentClassNames(baseClass, variants, className, options = {}) {
    const classes = [baseClass];
    const theme = getTheme();
    classes.push(...buildResponsiveVariantClassNames(baseClass, variants, options.fallbackVariants, theme));
    if (className) {
        classes.push(className);
    }
    return uniqueSpaceSeparated(classes.join(' '));
}
export function resolveResponsivePropValue(value, fallbackValue) {
    return resolveResponsiveValue(value, fallbackValue, getTheme());
}
export function withResponsiveBaseFallbackForTheme(value, fallbackValue) {
    return withResponsiveBaseFallback(value, fallbackValue, getTheme());
}
export { withResponsiveBaseFallbackForTheme as withResponsiveBaseFallback };
function uniqueSpaceSeparated(value) {
    const tokens = value
        .split(/\s+/)
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0);
    return Array.from(new Set(tokens)).join(' ');
}
function mergeStaticComponentClassNames(generatedClassName, renderedClassName) {
    const generatedClassNameValue = classNameValueToString(generatedClassName);
    const renderedClassNameValue = classNameValueToString(renderedClassName);
    if (!renderedClassNameValue) {
        return uniqueSpaceSeparated(generatedClassNameValue);
    }
    if (!generatedClassNameValue) {
        return uniqueSpaceSeparated(renderedClassNameValue);
    }
    return uniqueSpaceSeparated(`${generatedClassNameValue} ${renderedClassNameValue}`);
}
function classNameValueToString(value) {
    if (typeof value === 'string') {
        return value;
    }
    if (Array.isArray(value)) {
        return value.map(classNameValueToString).filter(Boolean).join(' ');
    }
    return '';
}
export function mergeComponentClassNames(generatedClassName, renderedClassName) {
    if (isReactiveAccessor(generatedClassName)) {
        return computed(() => mergeStaticComponentClassNames(generatedClassName(), isReactiveAccessor(renderedClassName) ? renderedClassName() : renderedClassName));
    }
    if (isReactiveAccessor(renderedClassName)) {
        return computed(() => mergeStaticComponentClassNames(generatedClassName, renderedClassName()));
    }
    return mergeStaticComponentClassNames(generatedClassName, renderedClassName);
}
function mergeStaticComponentStyleValues(managedStyle, renderedStyle) {
    const renderedStyleString = styleValueToString(renderedStyle);
    if (!renderedStyleString) {
        return managedStyle;
    }
    if (!managedStyle) {
        return renderedStyleString;
    }
    if (renderedStyleString === managedStyle) {
        return managedStyle;
    }
    return `${renderedStyleString}; ${managedStyle}`;
}
export function mergeComponentStyleValues(generatedStyle, renderedStyle) {
    if (isReactiveAccessor(renderedStyle)) {
        return computed(() => mergeStaticComponentStyleValues(generatedStyle, renderedStyle()));
    }
    return mergeStaticComponentStyleValues(generatedStyle, renderedStyle);
}
const UNSUPPORTED_DEFINE_COMPONENT_ROOT_TYPES = new Set([
    'Fragment',
    'TextNode',
    'Show',
    'For',
    'Match',
    'SwitchFlow',
]);
export function finalizeDefinedComponentNode(componentId, node, managed) {
    if (UNSUPPORTED_DEFINE_COMPONENT_ROOT_TYPES.has(node.type)) {
        throw new Error(`[smoothie/ui] defineComponent("${componentId}") render must return a concrete root element, not ${node.type}.`);
    }
    const hasExplicitChildren = node.children.length > 0 || node.text !== undefined;
    const shouldInjectChildren = managed.childrenMode !== 'manual' && !hasExplicitChildren;
    return {
        ...node,
        props: {
            ...node.props,
            className: mergeComponentClassNames(managed.className, node.props.className),
            style: mergeComponentStyleValues(managed.style, node.props.style),
        },
        children: shouldInjectChildren ? managed.children : node.children,
    };
}
//# sourceMappingURL=internal.js.map